/**
 * ForcedPasswordChangeV2 — v2 reskin of auth/ForcedPasswordChange (UI-v2 Phase 1e).
 *
 * Logic verbatim from v1 (useChangeMyPassword, queryClient.invalidate, detectHost).
 * Presentation only: AuthShellV2 + shadcn primitives + FormFieldV2.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useChangeMyPassword } from "@/hooks/queries/auth/useProfile";
import { useToastStore } from "@/stores/useToastStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { detectHost } from "@/lib/host";
import { queryClient } from "@/lib/queryClient";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormFieldV2 } from "@/components/v2/patterns/FormV2";

const PASSWORD_MIN = 12;

export default function ForcedPasswordChangeV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const changePw = useChangeMyPassword();
  const addToast = useToastStore((state) => state.addToast);
  const storeSlug = useAuthStore((state) => state.tenantSlug);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldError(null);

    if (newPassword.length < PASSWORD_MIN) {
      setFieldError(`New password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError("Passwords do not match.");
      return;
    }

    changePw.mutate(
      { current_password: currentPassword, new_password: newPassword },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
          addToast("Password updated. Welcome in.", "success");
          // The tenant branch lands in the v2 shell, which post-flip (PR4) is the
          // tenant ROOT — hence no `/v2` segment. The admin host has no v2 tree,
          // so it keeps the v1 /dashboard target.
          //
          // Not `useTenantBase()`: this page is reached BOTH at
          // `/:tenantSlug/change-password` (param bound) and — via the apiClient
          // 403 interceptor on the admin host — with no slug param at all, so the
          // store fallback is load-bearing rather than defensive.
          const dest =
            detectHost() === "admin"
              ? "/dashboard"
              : `/${tenantSlug ?? storeSlug ?? ""}/dashboard`;
          navigate(dest, { replace: true });
        },
        onError: (err) => {
          switch (err.error) {
            case "invalid_credentials":
              setFieldError("That temporary password is incorrect.");
              break;
            case "weak_password": {
              const reasons = Array.isArray(err.details["reasons"])
                ? (err.details["reasons"] as string[]).join(", ")
                : "Password is too weak.";
              setFieldError(reasons);
              break;
            }
            default:
              setFieldError(err.message || "That didn't go through. Try again.");
          }
        },
      }
    );
  };

  return (
    <AuthShellV2
      title="Set a new password"
      subtitle={`Your account uses a temporary password. Choose a new one — at least ${PASSWORD_MIN} characters.`}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormFieldV2 label="Temporary password" htmlFor="currentPassword-v2">
          <Input
            id="currentPassword-v2"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </FormFieldV2>
        <FormFieldV2 label="New password" htmlFor="newPassword-v2">
          <Input
            id="newPassword-v2"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            aria-invalid={!!fieldError || undefined}
          />
        </FormFieldV2>
        <FormFieldV2
          label="Confirm new password"
          htmlFor="confirmPassword-v2"
          error={fieldError ?? undefined}
        >
          <Input
            id="confirmPassword-v2"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            aria-invalid={!!fieldError || undefined}
          />
        </FormFieldV2>
        <Button
          type="submit"
          className="w-full"
          disabled={changePw.isPending}
          aria-busy={changePw.isPending}
        >
          {changePw.isPending ? "Updating…" : "Set new password"}
        </Button>
      </form>
    </AuthShellV2>
  );
}
